<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import mfaAPI from 'dashboard/api/mfa';
import { useAlert } from 'dashboard/composables';
import Auth from 'dashboard/api/auth';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MfaSetupWizard from '../settings/profile/MfaSetupWizard.vue';

const { t } = useI18n();

const provisioningUri = ref('');
const qrCodeUrl = ref('');
const secretKey = ref('');
const backupCodes = ref([]);
const setupWizardRef = ref(null);

const startMfaSetup = async () => {
  try {
    const response = await mfaAPI.enable();
    provisioningUri.value =
      response.data.provisioning_uri || response.data.provisioning_url;
    if (response.data.qr_code_url) {
      qrCodeUrl.value = response.data.qr_code_url;
    }
    secretKey.value = response.data.secret;
    backupCodes.value = [];
  } catch (error) {
    useAlert(t('MFA_SETTINGS.SETUP.ERROR_STARTING'));
  }
};

onMounted(startMfaSetup);

const verifyCode = async verificationCode => {
  try {
    const response = await mfaAPI.verify(verificationCode);
    if (response.data.backup_codes) {
      backupCodes.value = response.data.backup_codes;
    }
    return true;
  } catch (error) {
    setupWizardRef.value?.handleVerificationError(
      error.response?.data?.error || t('MFA_SETTINGS.SETUP.INVALID_CODE')
    );
    throw error;
  }
};

const completeMfaSetup = () => {
  useAlert(t('MFA_SETTINGS.SETUP.SUCCESS'));
  window.location.reload();
};

const handleLogout = () => {
  Auth.logout();
};
</script>

<template>
  <div
    class="flex flex-col items-center justify-center w-full h-full gap-6 p-6 bg-n-slate-2"
  >
    <div class="w-full max-w-md">
      <div class="text-center mb-6">
        <h1 class="text-lg font-medium text-n-slate-12 mb-2">
          {{ $t('MFA_SETTINGS.SETUP_REQUIRED.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ $t('MFA_SETTINGS.SETUP_REQUIRED.DESCRIPTION') }}
        </p>
      </div>

      <MfaSetupWizard
        ref="setupWizardRef"
        :show-setup="true"
        :mfa-enabled="false"
        :provisioning-uri="provisioningUri"
        :secret-key="secretKey"
        :backup-codes="backupCodes"
        :qr-code-url-prop="qrCodeUrl"
        @verify="verifyCode"
        @complete="completeMfaSetup"
      />

      <div class="flex justify-center mt-6">
        <NextButton
          variant="clear"
          color-scheme="secondary"
          :label="$t('APP_GLOBAL.NO_ACCOUNTS.LOGOUT')"
          @click="handleLogout"
        />
      </div>
    </div>
  </div>
</template>
